import { DatePipe, NgIf, NgClass, NgOptimizedImage, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';

import { AuthenticationService } from '@app/core';
import { RestService } from '@app/core/api/rest.service';
import { DeviceService } from '@app/core/services/device.service';
import { FileDownloadService } from '@app/core/services/file-download.service';
import { LoaderService } from '@app/core/services/loader.service';
import { RouterService } from '@app/core/services/router.service';
import { SwUpdateService } from '@app/core/services/sw.update.service';
import { SessionQuery } from '@app/core/state/session';
import { UserSettingsService } from '@app/core/state/user-settings';
import { RightUtil } from '@app/core/util/right.util';
import { StaticPathUtil } from '@app/core/util/static-path.util';
import { ContactSearchDialogComponent } from '@app/shared/dialog/contact-search-dialog/contact-search-dialog.component';
import { PortalInformationDialogComponent } from '@app/shared/dialog/portal-information-dialog/portal-information-dialog.component';
import { GlobalSidenavComponent } from '@app/shared/global-sidenav/global-sidenav.component';
import { globalDrawerItems } from '@app/shell/navitems';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Benutzer, RightsConst } from '@pezi/entities';
import { take } from 'rxjs/operators';
import packageInfo from '../../../package.json';
import { ChangelogService } from '../core/api/changelog.service';
import { ChangelogOverlayComponent } from '../shared/changelog/changelogoverlay.component';
import { TextSmallDirective } from '../core/directive/text-small.directive';
import { PeziButtonComponent } from '../shared/pezi-button/pezi-button.component';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { FastSearchComponent } from './fast-search/fast-search.component';
import { HasRightDirective } from '../shared/directive/hasright.directive';
import { PersonContainerComponent } from '../shared/partial-pages/person-container/person-container.component';
import { MatDivider } from '@angular/material/divider';
import { MatSelectionList, MatListItem, MatListItemIcon } from '@angular/material/list';
import { TabBarComponent } from '../shared/tab-bar/tab-bar.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { FooterVersionSelectComponent } from '../shared/footer-version-select/footer-version-select.component';

@UntilDestroy()
@Component({
  selector:        'app-shell',
  templateUrl:     './shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone:      true,
  imports:         [
    NgIf,
    TextSmallDirective,
    PeziButtonComponent,
    NgClass,
    MatIconButton,
    MatIcon,
    MatTooltip,
    NgOptimizedImage,
    FastSearchComponent,
    HasRightDirective,
    NgStyle,
    PersonContainerComponent,
    MatDivider,
    MatSelectionList,
    MatListItem,
    MatListItemIcon,
    GlobalSidenavComponent,
    TabBarComponent,
    RouterOutlet,
    LoaderComponent,
    FooterVersionSelectComponent
  ]
})
export class ShellComponent implements OnInit {

  globalDrawerItems = globalDrawerItems;
  public helpOverlayOpen = false;
  public userOverlayOpen = false;
  public datePipe = new DatePipe('de-AT');
  public clientVersion: string = packageInfo.version;

  public showSearch = false;
  public globalLoading = false;
  public updateApp = false;
  public kontaktRight = RightsConst.PZ_KONTAKT;
  public fastsearchRight = RightsConst.PZ_FASTSEARCH;

  public versionNotificationNeeded = false;
  public handbuchUrl: string;

  private static INFO_DIALOG_SEEN_KEY = 'infodialog-shown';

  public stage: string;

  @ViewChild('mainSideNav', {static: false}) mainSideNav: GlobalSidenavComponent;

  constructor(
    private router: Router,
    private titleService: Title,
    private authenticationService: AuthenticationService,
    private sessionQuery: SessionQuery,
    public deviceService: DeviceService, public routerService: RouterService, private cd: ChangeDetectorRef,
    private loaderService: LoaderService, public staticPath: StaticPathUtil, private swUpdateService: SwUpdateService,
    public contactDialog: MatDialog, private changelogService: ChangelogService, private changelogDialog: MatDialog, private rightUtil: RightUtil,
    private dialog: MatDialog, private restService: RestService, private fileDownloadService: FileDownloadService,
    private settingService: UserSettingsService
  ) {
    this.deviceService
      .getDeviceSubject()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.cd.markForCheck();
      });
  }

  ngOnInit() {
    switch (window.location.hostname) {
      case 'testvm1.nv.at':
        this.stage = 'dev';
        break;
      case 'ds260.nv.at':
        this.stage = 'test';
        break;
      case 'ds460.nv.at':
        this.stage = 'qa';
        break;
    }

    if (this.loggedInUser && !localStorage.getItem(ShellComponent.INFO_DIALOG_SEEN_KEY)) {
      this.dialog.open(PortalInformationDialogComponent, {data: this.loggedInUser?.flags?.maklerInfoOnFirstLogin});
      localStorage.setItem(ShellComponent.INFO_DIALOG_SEEN_KEY, this.datePipe.transform(new Date(), 'dd.MM.yyyy hh:mm:ss'));
    }

    this.loaderService.isLoading.pipe(untilDestroyed(this))
      .subscribe(val => {
        this.globalLoading = val;
      });
    this.swUpdateService
      .monitorServiceWorkerUpdate()
      .pipe(untilDestroyed(this))
      .subscribe(val => {
        // swUpdate.available has been replaced in Angular 17. Now Version event needs filtering. https://stackoverflow.com/questions/70684682/angular-swupdate-activated-deprecation
        if (val.type === 'VERSION_READY') {
          this.updateApp = true;
          this.cd.markForCheck();
        }
      });
    this.changelogService.versionsAvailable.pipe(untilDestroyed(this))
      .subscribe((val: boolean) => {
        this.versionNotificationNeeded = val;
        this.cd.markForCheck();
      });
    this.changelogService.getVersions(0)
      .subscribe();

  // Settings initial laden
    this.settingService.getSettings().pipe(take(1), untilDestroyed(this)).subscribe();
  }

  public async dlHandbuch() {
    await this.fileDownloadService.showOrDownloadFile(this.restService.getHandbuch());
  }

  public getMainClass(): string {
    const deviceClass = this.deviceService.generateDeviceClass();
    return this.updateApp ? deviceClass + ' with-update' : deviceClass;
  }

  public goToSettings(): void {
    this.closeOverlays();
    this.routerService.navigateSettings();
  }

  public closeHelpOverlay(): void {
    this.helpOverlayOpen = false;
    this.cd.markForCheck();
  }

  public closeOverlays(): void {
    this.userOverlayOpen = false;
    this.helpOverlayOpen = false;
    this.cd.markForCheck();
  }

  public reloadPage() {
    window.location.reload();
    this.updateApp = false;
  }

  async logout(): Promise<void> {
    await this.authenticationService.logout();
    await this.router.navigate(['/login'], {replaceUrl: true});
  }

  get loggedInUser(): Benutzer {
    return this.sessionQuery.getLoggedInUser();
  }

  get username(): string | null {
    const user = this.sessionQuery.getLoggedInUser();
    return user ? ((user.vorname ? user.vorname : '') + ' ' + (user.name ? user.name : '')) : null;
  }

  get usermail(): string | null {
    const user = this.sessionQuery.getLoggedInUser();
    return user ? user.loginEmail : null;
  }

  get title(): string {
    return this.titleService.getTitle();
  }

  public openUserMenu() {
    this.userOverlayOpen = !this.userOverlayOpen;
    if (this.helpOverlayOpen) {
      this.helpOverlayOpen = false;
    }
  }

  public openHelpMenu() {
    this.helpOverlayOpen = !this.helpOverlayOpen;
    if (this.userOverlayOpen) {
      this.userOverlayOpen = false;
    }
  }

  public openContactSearch() {
    const dialogRef = this.contactDialog.open(ContactSearchDialogComponent, {
      data: true
    });
  }

  public openChangelogOverlay() {
    this.changelogDialog.open(ChangelogOverlayComponent);
  }

  public getUserImagePath(): string {
    return this.staticPath.getPersonImgPath() + this.loggedInUser.image;
  }

  public getStageText(): string {
    switch (this.stage) {
      case 'dev':
        return 'DEV';
      case 'test':
        return 'TEST';
      case 'qa':
        return 'QA';
      default:
        return '';
    }
  }
}
